import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Award, Compass, FileCheck2, HardHat, ClipboardCheck } from "lucide-react";
import Seo from "@/components/Seo";
import Marquee from "@/components/Marquee";
import Reveal from "@/components/Reveal";
import ServiceCard from "@/components/ServiceCard";
import ReportMockup from "@/components/ReportMockup";
import PromoVideoSection from "@/components/PromoVideoSection";
import CtaSection from "@/components/CtaSection";
import { AUDIENCES, IMAGES, SERVICES, STEPS } from "@/data/site";

const HEADLINE_LINES = ["Independent Civil", "Engineering &", "Construction Consultancy"];

const PILLARS = [
    { icon: Award, title: "38 Years Experience", text: "Practical civil works experience across construction, maintenance, billing and landscaping." },
    { icon: Compass, title: "Independent Advice", text: "No execution contracts, no contractor ties — advice focused only on your interest." },
    { icon: FileCheck2, title: "Technical Review", text: "BOQs, bills, estimates and workmanship reviewed with a trained, experienced eye." },
    { icon: HardHat, title: "Practical Site Support", text: "On-site inspection, documentation and recommendations you can act on immediately." },
];

const SECOND_OPINION_STEPS = [
    { number: "01", title: "Share Your Documents", text: "Quotations, BOQs, contractor bills, drawings or photographs — send what you have." },
    { number: "02", title: "Site / Technical Review", text: "An independent review of documents, and of visible site conditions where a visit is agreed." },
    { number: "03", title: "Receive a Professional Report", text: "Documented observations and practical recommendations, delivered with a unique Report ID." },
];

export default function Home() {
    const heroRef = useRef(null);
    const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
    const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);

    return (
        <div>
            <Seo
                title="The Sens Civil Works Consultancy | Civil Engineering Consultancy in Gorakhpur"
                description="Independent civil engineering & construction consultancy in Gorakhpur, UP. 38 years of practical experience in construction, renovation, site inspection, BOQ & contractor bill review."
            />

            {/* ---------- HERO ---------- */}
            <section ref={heroRef} className="noise-overlay relative overflow-hidden bg-navy" data-testid="hero-section">
                <div className="blueprint-grid-dark absolute inset-0" aria-hidden="true" />
                <div className="relative mx-auto grid max-w-7xl grid-cols-1 gap-12 px-6 pb-20 pt-16 lg:grid-cols-12 lg:items-center lg:px-12 lg:pb-28 lg:pt-24">
                    <div className="lg:col-span-7">
                        <motion.p
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.6, delay: 0.1 }}
                            className="font-mono text-[11px] uppercase tracking-[0.3em] text-amber"
                            data-testid="hero-overline"
                        >
                            Gorakhpur · Uttar Pradesh · Est. 38 Yrs
                        </motion.p>
                        <h1 className="mt-6 font-display text-4xl font-bold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl">
                            {HEADLINE_LINES.map((line, i) => (
                                <span key={line} className="block overflow-hidden pb-1">
                                    <motion.span
                                        className="block"
                                        initial={{ y: "110%" }}
                                        animate={{ y: 0 }}
                                        transition={{ duration: 0.9, delay: 0.2 + i * 0.13, ease: [0.22, 1, 0.36, 1] }}
                                    >
                                        {i === 0 ? (
                                            <>
                                                <span className="text-amber">Independent</span> Civil
                                            </>
                                        ) : (
                                            line
                                        )}
                                    </motion.span>
                                </span>
                            ))}
                        </h1>
                        <motion.p
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7, delay: 0.7 }}
                            className="mt-6 max-w-xl text-base leading-relaxed text-slate-300 lg:text-lg"
                            data-testid="hero-subheadline"
                        >
                            38 Years of Practical Experience in Construction, Renovation, Maintenance &amp; Technical
                            Review. Independent, practical guidance to help you make better decisions — before, during
                            and after civil construction work.
                        </motion.p>
                        <motion.div
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7, delay: 0.85 }}
                            className="mt-9 flex flex-wrap gap-4"
                        >
                            <Link
                                to="/contact"
                                className="inline-flex items-center gap-2 bg-amber px-7 py-4 font-mono text-xs font-semibold uppercase tracking-[0.18em] text-white transition-colors duration-300 hover:bg-amber-hover"
                                data-testid="hero-book-consultation-btn"
                            >
                                Book a Consultation <ArrowRight size={14} />
                            </Link>
                            <Link
                                to="/site-inspection"
                                className="inline-flex items-center gap-2 border border-slate-500 px-7 py-4 font-mono text-xs font-semibold uppercase tracking-[0.18em] text-white transition-colors duration-300 hover:border-amber hover:text-amber"
                                data-testid="hero-request-inspection-btn"
                            >
                                Request Site Inspection
                            </Link>
                        </motion.div>
                    </div>

                    <div className="lg:col-span-5">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.96 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
                            className="tick-frame relative"
                        >
                            <motion.img
                                src={IMAGES.hero.url}
                                alt={IMAGES.hero.alt}
                                style={{ y: imgY }}
                                className="aspect-[4/5] w-full scale-110 object-cover"
                                data-testid="hero-image"
                            />
                            <div className="absolute bottom-4 left-4 border border-slate-600 bg-navy/90 px-4 py-2 backdrop-blur-sm">
                                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-300">
                                    Site Review — Gorakhpur, UP
                                </p>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            <Marquee />

            {/* ---------- TRUST ---------- */}
            <section className="blueprint-grid bg-background py-20 lg:py-28" data-testid="trust-section">
                <div className="mx-auto max-w-7xl px-6 lg:px-12">
                    <Reveal>
                        <p className="font-mono text-xs uppercase tracking-[0.25em] text-amber">Why Independent Matters</p>
                        <h2 className="mt-4 max-w-2xl font-display text-3xl font-bold tracking-tight text-navy sm:text-4xl lg:text-5xl">
                            Your Construction. Our Expertise.
                        </h2>
                    </Reveal>
                    <div className="mt-14 grid grid-cols-1 gap-px border border-slate-200 bg-slate-200 sm:grid-cols-2 lg:grid-cols-4">
                        {PILLARS.map((p, i) => (
                            <Reveal key={p.title} delay={i * 0.08} className="bg-white">
                                <div className="h-full p-8">
                                    <p.icon size={26} strokeWidth={1.5} className="text-amber" />
                                    <h3 className="mt-5 font-display text-lg font-bold text-navy">{p.title}</h3>
                                    <p className="mt-2 text-sm leading-relaxed text-slate-600">{p.text}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* ---------- SERVICES ---------- */}
            <section className="border-t border-slate-200 bg-white py-20 lg:py-28" data-testid="services-section">
                <div className="mx-auto max-w-7xl px-6 lg:px-12">
                    <Reveal className="flex flex-wrap items-end justify-between gap-6">
                        <div>
                            <p className="font-mono text-xs uppercase tracking-[0.25em] text-amber">What We Do</p>
                            <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-navy sm:text-4xl lg:text-5xl">
                                Consultancy Services
                            </h2>
                        </div>
                        <Link
                            to="/services"
                            className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.18em] text-navy transition-colors duration-200 hover:text-amber"
                            data-testid="services-view-all-link"
                        >
                            View all services <ArrowRight size={14} />
                        </Link>
                    </Reveal>
                    <div className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
                        {SERVICES.map((s, i) => (
                            <ServiceCard key={s.title} service={s} index={i} />
                        ))}
                    </div>
                </div>
            </section>

            {/* ---------- SECOND OPINION ---------- */}
            <section className="noise-overlay relative bg-navy py-20 lg:py-28" data-testid="second-opinion-section">
                <div className="blueprint-grid-dark absolute inset-0" aria-hidden="true" />
                <div className="relative mx-auto max-w-7xl px-6 lg:px-12">
                    <Reveal>
                        <p className="font-mono text-xs uppercase tracking-[0.25em] text-amber">Before You Build</p>
                        <h2 className="mt-4 max-w-3xl font-display text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-5xl">
                            Get an Independent Second Opinion Before You Build
                        </h2>
                        <p className="mt-5 max-w-2xl text-base leading-relaxed text-slate-300">
                            Before approving a quotation, paying a contractor, starting renovation or accepting
                            completed work, obtain an independent technical review.
                        </p>
                    </Reveal>
                    <div className="mt-14 grid grid-cols-1 gap-px border border-slate-700 bg-slate-700 md:grid-cols-3">
                        {SECOND_OPINION_STEPS.map((s, i) => (
                            <Reveal key={s.number} delay={i * 0.1} className="bg-navy">
                                <div className="h-full p-8">
                                    <p className="font-mono text-sm font-semibold tracking-[0.2em] text-amber">{s.number}</p>
                                    <h3 className="mt-4 font-display text-xl font-bold text-white">{s.title}</h3>
                                    <p className="mt-3 text-sm leading-relaxed text-slate-300">{s.text}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                    <Reveal delay={0.2}>
                        <Link
                            to="/contact?service=Independent%20Review"
                            className="mt-12 inline-flex items-center gap-2 bg-amber px-7 py-4 font-mono text-xs font-semibold uppercase tracking-[0.18em] text-white transition-colors duration-300 hover:bg-amber-hover"
                            data-testid="second-opinion-cta"
                        >
                            Request Independent Review <ArrowRight size={14} />
                        </Link>
                    </Reveal>
                </div>
            </section>

            {/* ---------- PROMO VIDEO ---------- */}
            <PromoVideoSection />

            {/* ---------- REPORT MOCKUP ---------- */}
            <section className="bg-background py-20 lg:py-28" data-testid="report-section">
                <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-6 lg:grid-cols-12 lg:px-12">
                    <div className="lg:col-span-5">
                        <Reveal>
                            <p className="font-mono text-xs uppercase tracking-[0.25em] text-amber">Documentation</p>
                            <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-navy sm:text-4xl">
                                Every Review, Documented
                            </h2>
                            <p className="mt-5 text-base leading-relaxed text-slate-600">
                                Each engagement closes with a structured digital report — observations, photographic
                                evidence, priorities, recommended actions and next steps — issued with a unique Report
                                ID for your records.
                            </p>
                            <ul className="mt-8 space-y-3">
                                {[
                                    "Checklist-based observations with priorities",
                                    "Photographic evidence of visible conditions",
                                    "Clear recommended actions and next steps",
                                    "Unique Report ID for future reference",
                                ].map((item) => (
                                    <li key={item} className="flex items-start gap-3 text-sm text-slate-700">
                                        <ClipboardCheck size={16} className="mt-0.5 shrink-0 text-amber" />
                                        {item}
                                    </li>
                                ))}
                            </ul>
                        </Reveal>
                    </div>
                    <div className="lg:col-span-7">
                        <ReportMockup />
                    </div>
                </div>
            </section>

            {/* ---------- WHO WE HELP TEASER ---------- */}
            <section className="border-t border-slate-200 bg-white py-20 lg:py-28" data-testid="audiences-section">
                <div className="mx-auto max-w-7xl px-6 lg:px-12">
                    <Reveal>
                        <p className="font-mono text-xs uppercase tracking-[0.25em] text-amber">Who We Help</p>
                        <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-navy sm:text-4xl">
                            Built Around Your Situation
                        </h2>
                    </Reveal>
                    <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
                        {AUDIENCES.map((a, i) => (
                            <Reveal key={a.title} delay={i * 0.08} className="h-full">
                                <Link
                                    to="/who-we-help"
                                    className="group block h-full border border-slate-200 bg-background p-7 transition-colors duration-300 hover:border-navy"
                                    data-testid={`audience-card-${a.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                >
                                    <a.icon size={24} strokeWidth={1.5} className="text-navy transition-colors duration-300 group-hover:text-amber" />
                                    <h3 className="mt-5 font-display text-lg font-bold text-navy">{a.title}</h3>
                                    <p className="mt-2 line-clamp-3 text-sm leading-relaxed text-slate-600">{a.description}</p>
                                </Link>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* ---------- PROCESS TEASER ---------- */}
            <section className="blueprint-grid border-t border-slate-200 bg-background py-20 lg:py-28" data-testid="process-section">
                <div className="mx-auto max-w-7xl px-6 lg:px-12">
                    <Reveal className="flex flex-wrap items-end justify-between gap-6">
                        <div>
                            <p className="font-mono text-xs uppercase tracking-[0.25em] text-amber">Process</p>
                            <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-navy sm:text-4xl">
                                How It Works
                            </h2>
                        </div>
                        <Link
                            to="/how-it-works"
                            className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.18em] text-navy transition-colors duration-200 hover:text-amber"
                            data-testid="process-details-link"
                        >
                            See the full process <ArrowRight size={14} />
                        </Link>
                    </Reveal>
                    <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
                        {STEPS.map((s, i) => (
                            <Reveal key={s.number} delay={i * 0.08}>
                                <div className="border-t-2 border-navy pt-6">
                                    <p className="font-mono text-sm font-semibold tracking-[0.2em] text-amber">Step {s.number}</p>
                                    <h3 className="mt-3 font-display text-lg font-bold text-navy">{s.title}</h3>
                                    <p className="mt-2 text-sm leading-relaxed text-slate-600">{s.description}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <CtaSection />
        </div>
    );
}
